import { normalize } from '../core/vec';
import type { InputState } from '../core/types';

const JOY_RADIUS = 56;

/**
 * 桌面：WASD / 方向键。移动端：触屏任意位置出现动态摇杆。
 * 每帧由外层调用 sample() 取不可变快照。
 */
export class InputController {
  private keys = new Set<string>();
  private state: InputState = { moveX: 0, moveY: 0 };
  enabled = false;

  private touchId: number | null = null;
  private originX = 0;
  private originY = 0;
  private joyX = 0;
  private joyY = 0;

  constructor(
    private joystick: HTMLElement,
    private base: HTMLElement,
    private knob: HTMLElement,
  ) {
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
    window.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    window.addEventListener('pointermove', (e) => this.onPointerMove(e));
    window.addEventListener('pointerup', (e) => this.onPointerUp(e));
    window.addEventListener('pointercancel', (e) => this.onPointerUp(e));
  }

  sample(): InputState {
    let x = this.joyX;
    let y = this.joyY;
    if (this.enabled) {
      const k = this.keyVector();
      x += k.x;
      y += k.y;
    }
    const n = normalize(x, y);
    const l = Math.min(1, Math.hypot(x, y));
    this.state.moveX = n.x * l;
    this.state.moveY = n.y * l;
    return this.state;
  }

  reset(): void {
    this.keys.clear();
    this.joyX = 0;
    this.joyY = 0;
    this.touchId = null;
    this.joystick.classList.add('hidden');
  }

  private keyVector(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.keys.has('a') || this.keys.has('arrowleft')) x -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) x += 1;
    if (this.keys.has('w') || this.keys.has('arrowup')) y -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) y += 1;
    return { x, y };
  }

  private onKeyDown(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k.startsWith('arrow')) e.preventDefault();
    this.keys.add(k);
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.enabled || e.pointerType !== 'touch' || this.touchId !== null) return;
    // 点按钮（暂停等）不触发摇杆
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    this.touchId = e.pointerId;
    this.originX = e.clientX;
    this.originY = e.clientY;
    this.joyX = 0;
    this.joyY = 0;
    this.base.style.left = `${e.clientX}px`;
    this.base.style.top = `${e.clientY}px`;
    this.knob.style.transform = 'translate(0px, 0px)';
    this.joystick.classList.remove('hidden');
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.touchId !== e.pointerId) return;
    let dx = e.clientX - this.originX;
    let dy = e.clientY - this.originY;
    const l = Math.hypot(dx, dy);
    if (l > JOY_RADIUS) {
      dx = (dx / l) * JOY_RADIUS;
      dy = (dy / l) * JOY_RADIUS;
    }
    this.joyX = dx / JOY_RADIUS;
    this.joyY = dy / JOY_RADIUS;
    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.touchId !== e.pointerId) return;
    this.touchId = null;
    this.joyX = 0;
    this.joyY = 0;
    this.joystick.classList.add('hidden');
  }
}
